import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { sponsorAPI } from '../services/api';

const SponsorsContext = createContext({});

export const useSponsors = () => useContext(SponsorsContext);

export const SponsorsProvider = ({ children }) => {
  const [sponsors, setSponsors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSponsors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await sponsorAPI.getSponsors();
      const sponsorsData = result.success ? result.data : 
                          result.data ? result.data : 
                          Array.isArray(result) ? result : [];
      setSponsors(sponsorsData || []);
      return { success: true, data: sponsorsData };
    } catch (error) {
      console.error('Failed to fetch sponsors:', error);
      setError(error.message || 'Failed to fetch sponsors');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const result = await sponsorAPI.getCategories();
      const categoriesData = result.success ? result.data : 
                            result.data ? result.data : 
                            Array.isArray(result) ? result : [];
      setCategories(categoriesData || []);
      return { success: true, data: categoriesData };
    } catch (error) {
      console.error('Failed to fetch sponsor categories:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const getSponsor = async (id) => {
    try {
      const result = await sponsorAPI.getSponsor(id);
      return result;
    } catch (error) {
      console.error('Failed to get sponsor:', error);
      return { success: false, error: error.message };
    }
  };

  const addSponsor = async (sponsorData) => {
    try {
      const result = await sponsorAPI.createSponsor(sponsorData);
      if (result.success) {
        await fetchSponsors(); // Refresh the list
      }
      return result;
    } catch (error) {
      console.error('Failed to create sponsor:', error);
      return { success: false, error: error.message };
    }
  };

  const updateSponsor = async (id, sponsorData) => {
    try {
      const result = await sponsorAPI.updateSponsor(id, sponsorData);
      if (result.success) {
        await fetchSponsors(); // Refresh the list
      }
      return result;
    } catch (error) {
      console.error('Failed to update sponsor:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteSponsor = async (id) => {
    try {
      const result = await sponsorAPI.deleteSponsor(id);
      if (result.success) {
        setSponsors(prev => prev.filter(sponsor => sponsor.id !== id));
      }
      return result;
    } catch (error) {
      console.error('Failed to delete sponsor:', error);
      return { success: false, error: error.message };
    }
  };

  // Initialize on mount
  useEffect(() => {
    fetchSponsors();
    fetchCategories();
  }, [fetchSponsors, fetchCategories]);

  const value = {
    sponsors,
    categories,
    loading,
    error,
    fetchSponsors,
    fetchCategories,
    getSponsor,
    addSponsor,
    updateSponsor,
    deleteSponsor,
  };

  return (
    <SponsorsContext.Provider value={value}>
      {children}
    </SponsorsContext.Provider>
  );
};